import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Box } from '@mui/material';
import { FormDialog, IMAGE_FILE_ACCEPT, RhfFileInput } from '@core/components';
import { useIsMobile } from '@core/hooks/useIsMobile';
import { useProgressiveImageSrc } from '@core/hooks/useProgressiveImageSrc';
import type { Design } from '../../api/design';
import { designPresetsQuery } from '../../api/designPreset';
import {
  type DesignFormValues,
  designToFormValues,
  submitDesign,
} from '../utils/design';
import type { PresetFeatures } from '../utils/designPreset';
import { getImageFileUrl, getImageThumbnailUrl } from '../utils/image';
import { DESIGN_VESTMENT_TYPE_OPTIONS } from '../utils/vestment';
import { DesignFormFields } from './DesignFormFields';
import { DesignPresetsPane } from './DesignPresetsPane';

interface DesignWithPresetsDialogProps {
  onClose: () => void;
  design: Design;
  features: PresetFeatures;
  onSuccess?: (design: Design) => void;
  initialPresetId?: number;
}

const DesignWithPresetsDialog: React.FC<DesignWithPresetsDialogProps> = ({
  onClose,
  design,
  features,
  onSuccess,
  initialPresetId,
}) => {
  const isMobile = useIsMobile();
  const { data: presets = [] } = useQuery(designPresetsQuery(design.id));
  const defaultValues = useMemo(() => designToFormValues(design), [design]);
  const imagePreviewSrc = useProgressiveImageSrc(
    getImageThumbnailUrl(design),
    getImageFileUrl(design)
  );

  const onSubmit = async (
    data: DesignFormValues,
    methods: UseFormReturn<DesignFormValues>
  ) => {
    onSuccess?.(await submitDesign(data, design, methods));
  };

  return (
    <FormDialog<DesignFormValues>
      open
      onClose={onClose}
      title="Edit Design"
      maxWidth="md"
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText="Update"
      cancelText="Cancel"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <DesignFormFields
            vestmentTypeOptions={DESIGN_VESTMENT_TYPE_OPTIONS}
            vestmentTypeLocked={presets.length > 0}
          />
          <DesignPresetsPane
            design={design}
            features={features}
            initialPresetId={initialPresetId}
          />
        </Box>
        <Box sx={{ width: { md: 200 }, marginTop: -1, flexShrink: 0 }}>
          <RhfFileInput<DesignFormValues>
            name="image"
            label="Image"
            accept={IMAGE_FILE_ACCEPT}
            initialPreviewSrc={imagePreviewSrc}
            height={isMobile ? 200 : 340}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};

export default DesignWithPresetsDialog;
